import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { backToWebsiteModal, getRedirectUrl } from 'utilities/global';
import {
  isSdkSelector,
  isPreviewSelector,
  getMerchantLogo,
  getCancelRedirectUrl,
  getMerchantWebsite,
  isDemoSelector
} from 'redux/selectors/global';
import { useAnalytics } from 'hooks';
import LanguagePicker from 'components/containers/LanguagePicker/Picker';
import Icon, { IconName } from 'components/core/Icon/Icon';
import { MerchantLogo } from 'styles/shared';
import Button, { ButtonVariants } from 'components/core/Button/Button';
import { ThemeBreakpoints, ThemeFamilies } from 'models';
import ErrorAlert from 'components/widgets/ErrorAlert/ErrorAlert';
import { trackClickBack } from 'utilities/analytics/global';
import { BACK_BUTTON_TOOLTIP_TEXT } from 'consts';

export interface IHeaderProps {
  className?: string;
  beforeContent?: ReactNode;
  redirectUrl?: string;
  onClickBack?: () => void;
  withBack?: boolean;
  withLogo?: boolean;
  withModal?: boolean;
  noTranslations?: boolean;
  excludedLangs?: string[];
  disableBackButton?: boolean;
  enableErrorAlert?: boolean;
}

const Header = ({
  className,
  beforeContent,
  redirectUrl,
  onClickBack,
  withBack = true,
  withLogo = true,
  withModal = true,
  noTranslations,
  excludedLangs = [],
  disableBackButton = false,
  enableErrorAlert = true
}: IHeaderProps) => {
  const dispatch = useDispatch();
  const isSdk = useSelector(isSdkSelector);
  const isDemo = useSelector(isDemoSelector);
  const isPreview = useSelector(isPreviewSelector);
  const merchantLogo = useSelector(getMerchantLogo);
  const merchantWebsite = useSelector(getMerchantWebsite);
  const cancelUrl = useSelector(getCancelRedirectUrl);
  const urlToRedirect =
    redirectUrl ||
    getRedirectUrl({
      url: cancelUrl,
      merchantWebsite
    });

  const tracker = useAnalytics();

  const handleBackClick = () => {
    trackClickBack();
    if (onClickBack) {
      return onClickBack();
    }

    if (withModal) {
      return backToWebsiteModal({
        dispatch,
        gaData: {},
        tracker,
        redirectUrl: urlToRedirect
      });
    }

    if (!urlToRedirect) return;
    window.location.href = urlToRedirect;
  };

  const renderMerchantLogo = () => (
    <LogoWrapper withBack={withBack}>
      <StyledMerchantLogo src={merchantLogo} />
    </LogoWrapper>
  );

  const showMerchantLogo = Boolean(!isSdk && withLogo && merchantLogo);

  const ButtonComp = isDemo ? DemoButton : Button;

  return (
    <Wrapper className={className}>
      <HeaderCompContent isPreview={isPreview} data-testid="web_header">
        <ButtonsWrapper>
          {withBack && urlToRedirect && (
            <ButtonComp
              name="back_to_website"
              staticSize={ThemeBreakpoints.MEDIUM}
              themeFamily={ThemeFamilies.GREY}
              variant={ButtonVariants.TEXT}
              onClick={isDemo ? undefined : handleBackClick}
              fixedHeight={false}
              disabled={disableBackButton}
              tooltipProps={isDemo && { themeFamily: ThemeFamilies.PRIMARY }}
              tooltip={isDemo && BACK_BUTTON_TOOLTIP_TEXT}
            >
              <ArrowLeftIcon name={IconName.ARROW_LEFT} />
            </ButtonComp>
          )}
          {showMerchantLogo && renderMerchantLogo()}
        </ButtonsWrapper>
        {!noTranslations && <StyledLanguagePicker excludedLangs={excludedLangs} />}
      </HeaderCompContent>
      {beforeContent}
      {enableErrorAlert && <ErrorAlert withAnimation />}
    </Wrapper>
  );
};

export default Header;

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

export const HeaderCompContent = styled.div<{ isPreview: boolean }>`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: ${({ theme }) => theme.view.isMobile && theme.colors.white};

  ${({ theme }) => {
    const { isMobileNotSdk } = theme.view;

    if (!isMobileNotSdk) {
      return css`
        padding-top: 2rem;
        padding-bottom: 1rem;
      `;
    }

    if (isMobileNotSdk) {
      return css`
          padding: 1rem 1.5rem;
          border-bottom: 0.1rem solid ${({ theme }) => theme.colors.surface3};
        }
      `;
    }
  }}
`;

const LogoWrapper = styled.div<{ withBack: boolean }>`
  position: absolute;
  ${({ theme, withBack }) => {
    if (!withBack) {
      return css`
        left: unset;
      `;
    }

    const { isMobile } = theme.view;

    if (isMobile) {
      return css`
        left: 12%;
      `;
    }

    return css`
      margin-left: 3.5rem;
    `;
  }}
`;

const StyledMerchantLogo = styled(MerchantLogo)`
  width: auto;
  max-width: 12rem;
  max-height: 4rem;
`;

const ButtonsWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ArrowLeftIcon = styled(Icon)`
  font-size: 2.3rem;
`;

const StyledLanguagePicker = styled(LanguagePicker)`
  justify-content: flex-end;
`;

const DemoButton = styled(Button)`
  .tooltip-container {
    width: 20rem;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;
